import * as THREE from 'three';

/**
 * Engine — the spine. Owns the renderer, the scene graph root, the camera rig,
 * and a fixed-timestep system scheduler.
 *
 * Systems are plain objects implementing any subset of:
 *   name      : string                       (required, unique)
 *   priority  : number                        (lower runs earlier, default 100)
 *   init(ctx) : Promise<void> | void
 *   fixed(dt, ctx)  : simulation step, always dt = 1/SIM_HZ
 *   update(dt, ctx) : per-rendered-frame step (interpolation, cosmetics)
 *   render(ctx)     : called instead of the default render, only for the
 *                     single system that sets `ownsRender = true`
 *   resize(w, h, ctx)
 *   dispose()
 */

export const SIM_HZ = 120;
export const SIM_DT = 1 / SIM_HZ;
const MAX_FRAME_DT = 0.25;

export class Engine {
  constructor(canvas, config = {}) {
    this.canvas = canvas;
    this.config = config;

    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false, // resolved by the post pipeline (TAA/SMAA)
      alpha: false,
      stencil: false,
      depth: true,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: true, // required for headless screenshot capture
    });
    renderer.debug.checkShaderErrors = true;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping; // the post pipeline tonemaps
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.VSMShadowMap;
    renderer.shadowMap.autoUpdate = true;
    renderer.setClearColor(0x000000, 1);
    // Accumulate stats across all render passes in a frame; Engine.tick resets.
    renderer.info.autoReset = false;
    this.renderer = renderer;

    this.maxAnisotropy = renderer.capabilities.getMaxAnisotropy();

    this.scene = new THREE.Scene();
    this.scene.name = 'root';

    // The camera the post pipeline renders from. The player system drives it.
    this.camera = new THREE.PerspectiveCamera(80, 16 / 9, 0.02, 2000);
    this.camera.name = 'mainCamera';
    this.camera.rotation.order = 'YXZ';

    // Separate near-plane scene + camera so the viewmodel never clips into walls.
    this.viewScene = new THREE.Scene();
    this.viewScene.name = 'viewmodelScene';
    this.viewCamera = new THREE.PerspectiveCamera(60, 16 / 9, 0.002, 12);
    this.viewCamera.name = 'viewmodelCamera';

    this._last = 0;
    this.elapsed = 0;
    this.frame = 0;
    this.accumulator = 0;
    this.alpha = 0;
    this.running = false;
    this.deterministic = false;

    this.systems = [];
    this._byName = new Map();
    this._rafId = 0;

    this.events = new EventBus();

    /** Shared mutable context handed to every system. */
    this.ctx = {
      engine: this,
      renderer,
      scene: this.scene,
      camera: this.camera,
      viewScene: this.viewScene,
      viewCamera: this.viewCamera,
      events: this.events,
      config,
      time: { elapsed: 0, dt: 0, frame: 0, alpha: 0 },
      get: (name) => this._byName.get(name),
      quality: config.quality ?? 'ultra',
    };

    this._onResize = this._onResize.bind(this);
    window.addEventListener('resize', this._onResize);
    this._onResize();
  }

  add(system) {
    if (!system || !system.name) throw new Error('System needs a unique `name`');
    if (this._byName.has(system.name)) throw new Error(`Duplicate system: ${system.name}`);
    system.priority = system.priority ?? 100;
    this.systems.push(system);
    this._byName.set(system.name, system);
    this.systems.sort((a, b) => a.priority - b.priority);
    return system;
  }

  get(name) {
    return this._byName.get(name);
  }

  async init() {
    for (const s of this.systems) {
      if (s.init) await s.init(this.ctx);
    }
    this._renderOwner = this.systems.find((s) => s.ownsRender) || null;
    this._onResize();
  }

  _onResize() {
    const dpr = Math.min(window.devicePixelRatio || 1, this.config.maxPixelRatio ?? 2);
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h, false);
    const aspect = w / Math.max(1, h);
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
    this.viewCamera.aspect = aspect;
    this.viewCamera.updateProjectionMatrix();
    this.width = w;
    this.height = h;
    this.drawWidth = Math.floor(w * dpr);
    this.drawHeight = Math.floor(h * dpr);
    for (const s of this.systems) if (s.resize) s.resize(w, h, this.ctx);
  }

  start() {
    if (this.running) return;
    this.running = true;
    this._last = performance.now() / 1000;
    const loop = () => {
      this._rafId = requestAnimationFrame(loop);
      const now = performance.now() / 1000;
      const dt = Math.min(now - this._last, MAX_FRAME_DT);
      this._last = now;
      if (this.deterministic) return; // driven manually by the capture harness
      this.tick(dt);
    };
    this._rafId = requestAnimationFrame(loop);
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this._rafId);
  }

  /** One frame: N fixed simulation steps, then a variable-rate cosmetic update + render. */
  tick(dt) {
    this.renderer.info.reset();
    this.frame++;
    this.elapsed += dt;
    const t = this.ctx.time;
    t.dt = dt;
    t.elapsed = this.elapsed;
    t.frame = this.frame;

    this.accumulator += dt;
    let steps = 0;
    while (this.accumulator >= SIM_DT && steps < 8) {
      for (const s of this.systems) if (s.fixed) s.fixed(SIM_DT, this.ctx);
      this.accumulator -= SIM_DT;
      steps++;
    }
    if (steps === 8) this.accumulator = 0; // bail out of a death spiral
    this.alpha = this.accumulator / SIM_DT;
    t.alpha = this.alpha;

    for (const s of this.systems) if (s.update) s.update(dt, this.ctx);

    if (this._renderOwner) this._renderOwner.render(this.ctx);
    else {
      this.renderer.render(this.scene, this.camera);
      this.renderer.autoClear = false;
      this.renderer.render(this.viewScene, this.viewCamera);
      this.renderer.autoClear = true;
    }
  }

  /** Deterministic advance used by the screenshot harness. */
  step(dt = 1 / 60, count = 1) {
    for (let i = 0; i < count; i++) this.tick(dt);
  }

  dispose() {
    this.stop();
    window.removeEventListener('resize', this._onResize);
    for (const s of this.systems) if (s.dispose) s.dispose();
    this.renderer.dispose();
  }
}

export class EventBus {
  constructor() {
    this._m = new Map();
  }
  on(type, fn) {
    if (!this._m.has(type)) this._m.set(type, new Set());
    this._m.get(type).add(fn);
    return () => this.off(type, fn);
  }
  off(type, fn) {
    this._m.get(type)?.delete(fn);
  }
  emit(type, payload) {
    const s = this._m.get(type);
    if (!s) return;
    for (const fn of s) fn(payload);
  }
}
